import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { persistStore, persistReducer } from "redux-persist";
import localforage from "localforage";
import { authReducer } from "./Auth/Reducer/auth.reducer";
// import { quotationsReducer } from "./Admin/Reducer/quotations.reducer";
import { ordersReducer } from "./Admin/Reducer/orders.reducer";
import { staticDataReducer } from "./Admin/Reducer/staticData.reducer";
import productReducer from "./Admin/Reducer/productSlice";
import { adminSettingsReducer } from "./Admin/Reducer/adminSetting.reducer";
import { emailAndNotificationReducer } from "./Admin/Reducer/emailAndNotification.reducer";
import quotationReducer from "./Admin/Pages/Quotations/quotationSlice";
import categoriesReducer from "./Admin/Reducer/categoriesSlice";
import variantCatalogReducer from "./Admin/Reducer/variantCatalogSlice";
import { masterDataReducer } from "./Admin/Reducer/masterData.reducer";
import { teamMemberReducer } from "./Admin/Reducer/teammember.reducer";
import { dashboardReducer } from "./Admin/Reducer/dashboard.reducer";
import { staticPagesReducer } from "./Admin/Reducer/staticPages.reducer";
import { installationPlanReducer } from "./Admin/Reducer/installationPlan";
import { notificationReducer } from "./Admin/Reducer/notification.reducer";
import { salesCommissionReducer } from "./Admin/Reducer/salesCommission.reducer";
import { invoiceReducer } from "./Admin/Reducer/invoice.reducer";
import { SummaryDetailReducer } from "./Admin/Reducer/summaryDetail.reducer";
import { DeliveryPricingReducer } from "./Admin/Reducer/deliveryPricing.reducer";
import { countryReducer } from "./Admin/Reducer/country.reducer";
import { StockProcessManagerReducer } from "./Admin/Reducer/stockProcessManager.reducer";
import { sageReducer } from "./Admin/Reducer/sage.reducer";
import { loadNoticeReducer } from "./Admin/Reducer/loadNotice.reducer";
import { steelCoilManagerReducer } from "./Admin/Redux/steelCoilManager";
import { teamMemberCommissionReducer } from "./Admin/Reducer/teamMemberCommission.reducer";
import estimationReducer from "./Admin/Pages/Estimations/estimationSlice";
import quoteRequestReducer from "./Admin/Pages/QuoteRequests/quoteRequestSlice";
// import { authReducer } from "./Auth/Redux/Reducers/auth";

// Error Reducer Types
interface ErrorState {
  authError?: Record<string, string>;
}

interface SetErrorActionPayload {
  scope: string;
  status: {
    param: string;
    msg: string;
    path?: string;
    nestedErrors?: { param: string; msg: string }[];
  }[];
}

interface SetErrorAction {
  type: "SET_ERROR";
  payload: SetErrorActionPayload;
}

type ErrorAction = SetErrorAction;

const errorReducer = (
  state: ErrorState = {},
  action: ErrorAction,
): ErrorState => {
  switch (action.type) {
    case "SET_ERROR": {
      const temp: Record<string, string> = {};
      const { payload } = action;

      if (payload.status && payload.status.length) {
        payload.status.forEach((ele) => {
          if (ele.param === "_error") {
            ele.nestedErrors?.forEach((ele1) => {
              const key = ele1.param.split(".").pop()!;
              temp[key] = ele1.msg;
            });
          } else if (ele.param?.includes("updates.")) {
            const key = ele.param.split(".").slice(1).join(".");
            temp[key] = ele.msg;
          } else {
            temp[ele.path!] = ele.msg;
          }
        });
      }

      return { ...state, [`${payload.scope}Error`]: temp };
    }
    default:
      return state;
  }
};

// Loading Reducer Types
interface LoadingState {
  authLoading: boolean;
  welcomeDataLoading: boolean;
  [key: string]: boolean;
}

interface SetLoadingActionPayload {
  scope: string;
  status: boolean;
}

interface SetLoadingAction {
  type: "SET_LOADING";
  payload: SetLoadingActionPayload;
}

type LoadingAction = SetLoadingAction;

const loadingReducer = (
  state: LoadingState = { authLoading: false, welcomeDataLoading: false },
  action: LoadingAction,
): LoadingState => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        [`${action.payload.scope}Loading`]: action.payload.status,
      };
    default:
      return state;
  }
};

// Msg Reducer Types
interface MsgState {
  success?: string;
  error?: string;
  [key: string]: string | undefined;
}

interface SetToastActionPayload {
  scope: string;
  status?: string;
}

interface SetToastAction {
  type: "SET_TOAST";
  payload: SetToastActionPayload;
}

interface ClearToastAction {
  type: "CLEAR_TOAST";
}

type MsgAction = SetToastAction | ClearToastAction;

const msgReducer = (state: MsgState = {}, action: MsgAction): MsgState => {
  switch (action.type) {
    case "SET_TOAST":
      return {
        ...state,
        [`${action.payload.scope}`]:
          action.payload.status || "Something went wrong!",
      };
    case "CLEAR_TOAST":
      return { ...state, error: undefined, success: undefined };
    default:
      return state;
  }
};

// Combine reducers
const rootReducer = combineReducers({
  error: errorReducer,
  loading: loadingReducer,
  msg: msgReducer,

  auth: authReducer,
  dashboard: dashboardReducer,
  quotation: quotationReducer,
  orders: ordersReducer,
  staticData: staticDataReducer,
  product: productReducer,
  variantCatalog: variantCatalogReducer,
  adminSettings: adminSettingsReducer,
  email: emailAndNotificationReducer,
  masterData: masterDataReducer,
  teamMember: teamMemberReducer,
  staticPages: staticPagesReducer,
  installationPlan: installationPlanReducer,
  notification: notificationReducer,
  salesCommission: salesCommissionReducer,
  categories: categoriesReducer,
  invoice: invoiceReducer,
  summaryDetail: SummaryDetailReducer,
  deliveryPricing: DeliveryPricingReducer,
  country: countryReducer,
  stockProcessManager: StockProcessManagerReducer,
  sage: sageReducer,
  loadNotice: loadNoticeReducer,
  steelCoilManager: steelCoilManagerReducer,
  teamMemberCommission: teamMemberCommissionReducer,
  estimation: estimationReducer,
  quoteRequest: quoteRequestReducer,
});

const persistConfig = {
  key: "root",
  storage: localforage,
  whitelist: ["variantCatalog"], // Only persist these reducers
};

const persistedReducer = persistReducer(persistConfig, rootReducer as any);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Configure store
const store = configureStore({
  reducer: persistedReducer as any,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          "persist/REGISTER",
        ],
      },
    }),
});

export const persistor = persistStore(store);

export default store;
